import { useQuery } from "@tanstack/react-query";
import React from "react";
import useApi from "../../../../shared/hooks/useApi";
import {
  contributeAiCapabilityParamOption,
  declareAiCapability,
  useProvideAiCapability,
} from "../../../ai-omnibar/ai-capability-registry";
import { declareAiTabDestination } from "../../../ai-omnibar/ai-tab-destinations";

// The tab saying it is somewhere to go. Banked Minutes opens on View, so Jobs is only reachable by
// name if it says so itself.
declareAiTabDestination({
  id: "bankedMinutesJobs.open",
  title: "Open the Banked Minutes jobs tab",
  stateName: "app.banked_minutes.jobs",
  opens: "page:bankedMinutesJobs",
  description:
    "Opens Billing > Banked Minutes > Jobs: the runs that banked or released minutes, rather than the balances themselves.",
  location: "the tab bar of Banked Minutes",
  permissionKey: "view_billing_section_banked_minutes",
  examples: ["open banked minutes jobs"],
});

/** What the Banked Minutes view can do for the AI omnibar. */
declareAiCapability({
  id: "bankedMinutes.filter",
  title: "Filter the banked minutes list",
  effect: "prefill",
  description:
    "Sets the filters on Billing > Banked Minutes: the date range, office and contract. Only works while that page is open.",
  location: "the filter bar of the Billing > Banked Minutes page",
  scope: "page",
  permissionKey: "view_billing_section",
  params: [
    {
      name: "from",
      type: "date",
      required: false,
      description: "First day to include, as yyyy-MM-dd.",
    },
    {
      name: "to",
      type: "date",
      required: false,
      description: "Last day to include, as yyyy-MM-dd.",
    },
    {
      name: "office",
      type: "enum",
      required: false,
      multiple: true,
      description: "Limit to these offices.",
    },
    {
      name: "contract",
      type: "enum",
      required: false,
      multiple: true,
      description: "Limit to these contracts.",
    },
  ],
  examples: ["banked minutes last month", "banked minutes for the europe office"],
});

function useContributedOptions(
  paramName: string,
  rows: readonly { id: string | number; name: string }[] | undefined
): void {
  const key = (rows ?? []).map((row) => `${row.id}:${row.name}`).join("|");

  React.useEffect(() => {
    const releases = (rows ?? []).map((row) =>
      contributeAiCapabilityParamOption("bankedMinutes.filter", paramName, {
        value: String(row.id),
        label: row.name,
      })
    );

    return () => releases.forEach((release) => release());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramName, key]);
}

export function useBankedMinutesAiCapabilities(params: {
  /** The page's own filter setter, typed loosely: this hook only sets keys it declares. */
  setFilter: (key: string, value: unknown) => void;
}): void {
  const { setFilter } = params;
  const { queries } = useApi();

  const setFilterRef = React.useRef(setFilter);
  setFilterRef.current = setFilter;

  const offices = useQuery(queries.office.list());
  const contracts = useQuery(queries.contract.list());

  useContributedOptions("office", offices.data?.offices);
  useContributedOptions("contract", contracts.data?.contractTypes);

  useProvideAiCapability(
    "bankedMinutes.filter",
    (args, context) => {
      const applied: string[] = [];
      const asList = (value: unknown) => (Array.isArray(value) ? value : [value]);
      const label = (name: string) => context.labels[name] ?? String(args[name]);

      if (args.from !== undefined) {
        setFilterRef.current("from", String(args.from));
      }

      if (args.to !== undefined) {
        setFilterRef.current("to", String(args.to));
      }

      if (args.from !== undefined || args.to !== undefined) {
        applied.push([args.from, args.to].filter(Boolean).join(" to "));
      }

      for (const [arg, key] of [
        ["office", "officeIds"],
        ["contract", "contractTypeIds"],
      ] as const) {
        if (args[arg] === undefined) {
          continue;
        }

        setFilterRef.current(
          key,
          asList(args[arg]).map((one) => Number(one))
        );
        applied.push(label(arg));
      }

      return applied.length === 0
        ? "Re-applied the banked minutes filters"
        : `Showing banked minutes: ${applied.join(", ")}`;
    },
    { enabled: offices.isSuccess && contracts.isSuccess }
  );
}

/**
 * The Jobs tab beside it, which has its own date range over the jobs that produced those minutes.
 * A separate id because the two tabs filter different things and are never on screen together.
 */
declareAiCapability({
  id: "bankedMinutesJobs.filter",
  title: "Filter the banked minutes jobs list",
  effect: "prefill",
  description:
    "Sets the date range on Billing > Banked Minutes > Jobs, which filters the jobs that produced the banked minutes. Only works while that tab is open.",
  location: "the date range of the Billing > Banked Minutes > Jobs tab",
  scope: "page:bankedMinutesJobs",
  permissionKey: "view_billing_section",
  params: [
    {
      name: "from",
      type: "date",
      required: false,
      description: "First day to include, as yyyy-MM-dd.",
    },
    {
      name: "to",
      type: "date",
      required: false,
      description: "Last day to include, as yyyy-MM-dd.",
    },
  ],
  examples: ["banked minutes jobs from last week"],
});

export function useBankedMinutesJobsAiCapabilities(params: {
  /** The tab's own filter setter, typed loosely: this hook only sets keys it declares. */
  setFilter: (key: string, value: unknown) => void;
}): void {
  const { setFilter } = params;

  const setFilterRef = React.useRef(setFilter);
  setFilterRef.current = setFilter;

  useProvideAiCapability("bankedMinutesJobs.filter", (args) => {
    if (args.from !== undefined) {
      setFilterRef.current("from", String(args.from));
    }

    if (args.to !== undefined) {
      setFilterRef.current("to", String(args.to));
    }

    const range = [args.from, args.to].filter(Boolean).join(" to ");

    return range === "" ? "Re-applied the jobs filters" : `Showing banked minutes jobs ${range}`;
  });
}
